/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable react/prop-types */
import * as React from 'react';
import { MoreHorizontal } from 'lucide-react';

import { ButtonProps, buttonVariants } from '@/components/ui/button';
import { cn } from './utils';
import { ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/solid';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex list-none flex-row items-center gap-1', className)} {...props} />
  ),
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('list-none', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<'a'>;

const PaginationLink = ({ className, isActive, size = 'icon', ...props }: PaginationLinkProps) => (
  <a
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      buttonVariants({ variant: 'outline', size }),
      isActive && 'bg-people-teal-600 text-white hover:bg-people-teal-600 hover:text-white',
      className,
    )}
    {...props}
  />
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({ className, ...props }: Omit<React.ComponentProps<'a'>, 'children'>) => (
  <a
    aria-label="Go to previous page"
    className={cn(buttonVariants({ variant: 'outline', size: 'default' }), 'gap-1 border-[#173737] pl-2.5', className)}
    {...props}>
    <ArrowLeftIcon className="h-5 w-5 shrink-0 text-people-coral" aria-hidden />
    <span>Previous</span>
  </a>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({ className, ...props }: Omit<React.ComponentProps<'a'>, 'children'>) => (
  <a
    aria-label="Go to next page"
    className={cn(buttonVariants({ variant: 'outline', size: 'default' }), 'gap-1 border-[#173737] pr-2.5', className)}
    {...props}>
    <span>Next</span>
    <ArrowRightIcon className="h-5 w-5 shrink-0 text-people-coral" aria-hidden />
  </a>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span aria-hidden className={cn('flex h-9 w-9 items-center justify-center', className)} {...props}>
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
